import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN, formatoFecha, METODOS_PAGO } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function Pagos() {
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/pagos');
      setPagos(res.data);
    } catch { toast.error('Error al cargar pagos'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const totalPagos = pagos.reduce((sum, p) => sum + (p.monto || 0), 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="pagos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">Pagos</h1>
        <div className="flex items-center gap-2 bg-slate-900/40 border border-slate-800 rounded-sm px-4 py-2">
          <DollarSign className="w-4 h-4 text-amber-500" />
          <span className="text-xs text-slate-500 uppercase">Total:</span>
          <span className="text-sm font-bold text-amber-500 font-mono">{formatoMXN(totalPagos)}</span>
        </div>
      </div>

      {cargando ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 bg-slate-800" />)}</div>
      ) : pagos.length === 0 ? (
        <div className="text-center py-16" data-testid="pagos-empty">
          <DollarSign className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No hay pagos registrados</p>
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Orden</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Metodo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Referencia</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Monto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagos.map(p => (
                <TableRow key={p.id} className="border-slate-800/50 hover:bg-slate-800/40" data-testid={`pago-row-${p.id}`}>
                  <TableCell className="text-sm text-slate-400">{formatoFecha(p.fecha_pago)}</TableCell>
                  <TableCell className="font-mono text-xs text-amber-500">{p.numero_orden}</TableCell>
                  <TableCell className="text-sm text-slate-300 hidden md:table-cell">{p.cliente_nombre}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-slate-400 border-slate-700 text-xs capitalize">{p.metodo_pago}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 font-mono hidden lg:table-cell">{p.referencia || '-'}</TableCell>
                  <TableCell className="text-sm text-amber-500 font-mono font-bold text-right">{formatoMXN(p.monto)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
